import { Link } from 'react-router-dom';
import '../../css/FeaturesPage.css';

export const FeaturesPage = () => {
    return (
        <div className="features-page">
            {/* Hero */}
            <section className="features-hero">
                <div className="features-hero-container">
                    <div className="features-hero-content">
                        <div className="features-hero-badge">★ ALL MODULES · ONE PLATFORM</div>
                        <h1>Everything you need to <span className="underline-orange">run a fleet</span>, in one place.</h1>
                        <p>Inspections, preventive maintenance, tire tracking, cost reporting, and MSHA-ready training. Start with the core, add what you need, skip what you don't.</p>
                        <div className="features-hero-buttons">
                            <Link to="/register/free-demo" className="splash-btn-primary">Start Free 30-Day Demo</Link>
                            <Link to="/contact" className="splash-btn-outline dark">Contact Sales <span>→</span></Link>
                        </div>
                    </div>
                    <div className="features-hero-image">
                        <img src="/homepage-images/features/navigation bar and header.png" alt="Equipment crew" />
                    </div>
                </div>
            </section>

            {/* Tab Navigation */}
            <section className="features-tabs">
                <div className="features-tabs-container">
                    <div className="features-tab-row">
                        <span className="features-tab-label">CORE:</span>
                        <a href="#inspections" className="features-tab-link">Inspections</a>
                        <a href="#forms" className="features-tab-link">Forms</a>
                        <a href="#equipment" className="features-tab-link">Equipment</a>
                        <a href="#users" className="features-tab-link">Users</a>
                        <a href="#issues" className="features-tab-link">Issues</a>
                        <a href="#safety" className="features-tab-link">Safety Briefing</a>
                        <a href="#reports" className="features-tab-link">Reports</a>
                        <a href="#resources" className="features-tab-link">Resources</a>
                    </div>
                    <div className="features-tab-row addons-row">
                        <span className="features-tab-label addon">ADD-ONS:</span>
                        <a href="#pm-addon" className="features-tab-pill">Preventive Maintenance</a>
                        <a href="#tires-addon" className="features-tab-pill">Tire Management</a>
                        <a href="#cost-addon" className="features-tab-pill">Cost Tracking</a>
                        <a href="#training-addon" className="features-tab-pill">Safety & Training</a>
                        <a href="#skillsets-addon" className="features-tab-pill">User Skillsets</a>
                    </div>
                    <div className="features-tab-underline"></div>
                </div>
            </section>

            {/* Core Modules Intro */}
            <section className="features-core-intro">
                <div className="features-section-container">
                    <div className="features-core-label"><span className="orange-text">Core</span> Modules</div>
                    <p className="features-core-description">
                        The day-to-day workflows your crews already run: inspections, equipment, users, and reporting, all built into the core. No upsells for the basics.
                    </p>
                </div>
            </section>

            {/* Inspections */}
            <section id="inspections" className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>Inspections built for the field</h2>
                            <p>Print a QR code, stick it on the machine, scan it with a phone. The right form opens to the right asset. Inspector signs and submits in under two minutes.</p>
                            <ul className="splash-bullet-list check">
                                <li>QR-coded equipment opens straight to its inspection</li>
                                <li>Works with gloves, in bright sun, on a cracked screen</li>
                                <li>Photos, notes, and audio attached to every line item</li>
                                <li>Flagged items auto-create issues</li>
                                <li>Offline mode for remote pits and quarries</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-1.png" alt="Inspection interface" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Forms */}
            <section id="forms" className="features-detail-section alt">
                <div className="features-section-container">
                    <div className="features-detail-row reverse">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>Build the right form for every machine</h2>
                            <p>Inspection forms per equipment category. Daily service forms for the mobile staff. Drag items to create, edit, and rearrange. Always use the right checklist - crews always see the right checklist without searching.</p>
                            <ul className="splash-bullet-list check">
                                <li>Form library by equipment type: compactors, crushers, trucks</li>
                                <li>Drag-and-drop builder with conditional fields and sections</li>
                                <li>Daily service forms separate from full inspections for fast pen-shift checks</li>
                                <li>Versioning so old reports stay accurate even after forms change</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-2.png" alt="Form builder interface" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Equipment */}
            <section id="equipment" className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>Every asset, one profile</h2>
                            <p>A complete record per machine: inspections, services, costs, training, and history all tied to the same asset. Organize by site, category, or status.</p>
                            <ul className="splash-bullet-list check">
                                <li>Group by site, category, or active/retired status</li>
                                <li>Track unit of measure: hours, miles, cycles, to custom</li>
                                <li>Lifecycle status: in service, in repair, retired</li>
                                <li>Surfaces every inspection, service, expense, and issue</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-3.png" alt="Equipment profile" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Users */}
            <section id="users" className="features-detail-section alt">
                <div className="features-section-container">
                    <div className="features-detail-row reverse">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>The whole crew in one system</h2>
                            <p>Operators, mechanics, supervisors, and admins all live in the same platform. Every inspection, service, and training event ties back to the person who did it.</p>
                            <ul className="splash-bullet-list check">
                                <li>Three roles: Company Admin, Staff, and Super Admin</li>
                                <li>Permissions per module so field crews see only what they need</li>
                                <li>Full activity log: who did what, when, on which asset</li>
                                <li>Email invites and password resets handled in-app</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-4.png" alt="User management" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Issues */}
            <section id="issues" className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>Catch problems before they catch you</h2>
                            <p>When an inspector flags an item, an issue is created automatically and routed to the right person. Tracked from first report to final fix.</p>
                            <ul className="splash-bullet-list check">
                                <li>Auto-created from any flagged inspection item</li>
                                <li>Priority levels: low, medium, high and critical (down-equipment)</li>
                                <li>Track to closure</li>
                                <li>Linked back to the inspection, the asset, and the inspector</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-5.png" alt="Issue tracking" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Safety Briefing */}
            <section id="safety" className="features-detail-section alt">
                <div className="features-section-container">
                    <div className="features-detail-row reverse">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>A safety topic on every login</h2>
                            <p>Crews see a safety briefing the first time they log in each day. Manual topic rotation included free. Upgrade to Safety & Training for full scheduling.</p>
                            <ul className="splash-bullet-list check">
                                <li>Daily popup on first login, with acknowledgement tracking</li>
                                <li>Build a library of topics: PDFs, videos, or written content</li>
                                <li>Manually rotate the active topic when you want to push something new</li>
                                <li>Permanent record of every acknowledgement by user and date</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-6.png" alt="Safety briefing" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Reports */}
            <section id="reports" className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>Reports across everything you track</h2>
                            <p>Filter by date, asset, category, or user. Export to PDF or CSV. Every module's data is reportable, audit-ready, and one click away.</p>
                            <ul className="splash-bullet-list check">
                                <li>Inspection history, PM compliance, issue resolution times</li>
                                <li>Activity log by user: who did what, when, where</li>
                                <li>Saved report templates for monthly and quarterly reviews</li>
                                <li>PDF and CSV export for MSHA, insurance, and ownership reviews</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-7.png" alt="Reports dashboard" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Resources */}
            <section id="resources" className="features-detail-section alt">
                <div className="features-section-container">
                    <div className="features-detail-row reverse">
                        <div className="features-detail-content">
                            <span className="splash-section-label">● Core Module</span>
                            <h2>One source of truth for parts, vendors, and rates</h2>
                            <p>Service items, vendors, and labor rates live in Resources. PM tasks and cost entries reference them with a dropdown so you never type the same vendor name twice.</p>
                            <ul className="splash-bullet-list check">
                                <li>Service items library (filters, belts, hoses, bearings, etc.)</li>
                                <li>Vendor records with contact info and part numbers</li>
                                <li>Internal labor rates by role for accurate cost calculations</li>
                                <li>Update a rate once, every downstream report reflects it</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/core-module-8.png" alt="Resources management" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Add-On Modules Banner */}
            <section className="features-addon-banner">
                <div className="features-section-container">
                    <h2><span className="orange-text">Add-On</span> Modules</h2>
                    <p>Pay for what you use. Activate add-ons in minutes from the admin panel. Deactivate just as fast. No migration headache.</p>
                </div>
            </section>

            {/* PM Add-On */}
            <section className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">✦ Add-On</span>
                            <h2>Stop chasing PMs in a notebook</h2>
                            <p>Set service levels per equipment type, define the parts and procedures, and let the system tell you what's due. Due Soon, Overdue, and Scheduled queues at a glance.</p>
                            <ul className="splash-bullet-list check">
                                <li>Custom service levels, each with its own parts and procedures</li>
                                <li>Custom intervals by hours, days, miles, or cycles</li>
                                <li>Auto-email notifications for Due Soon and Overdue</li>
                                <li>Parts and vendors from Resources, with cost rolling into Cost Tracking</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/add-on-module-1.png" alt="PM Dashboard" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Tire Management Add-On */}
            <section className="features-detail-section alt">
                <div className="features-section-container">
                    <div className="features-detail-row reverse">
                        <div className="features-detail-content">
                            <span className="splash-section-label">✦ Add-On</span>
                            <h2>Every tire, tracked from install to replacement</h2>
                            <p>Build reusable axle templates, assign tires to positions, log tread depth on a live diagram. Replacement alerts fire when wear crosses your threshold.</p>
                            <ul className="splash-bullet-list check">
                                <li>Axle templates: 4x2, 6x4, 12x4, or build your own</li>
                                <li>Visual diagram of every position, color-coded by tread health</li>
                                <li>Tread depth tracked per inspection, plotted over time</li>
                                <li>Replacement alerts at custom tread percentage thresholds</li>
                                <li>Full lifecycle: install date, hours run, replacement, retread</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/add-on-module-2.png" alt="Tire management" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Cost Tracking Add-On */}
            <section className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">✦ Add-On</span>
                            <h2>Know what your equipment actually costs</h2>
                            <p>Log every expense against the asset that incurred it. PM, parts, labor, tires, repairs. Cost-per-UM reports tell you which assets are profitable and which ones are bleeding money.</p>
                            <ul className="splash-bullet-list check">
                                <li>Expense categories with vendor invoice attachment</li>
                                <li>Internal labor logged against the asset at the right rate</li>
                                <li>$/UM reports by date range, category, or site</li>
                                <li>PM and tire costs auto-roll-in from their modules</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/add-on-module-3.png" alt="Cost tracking" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Safety Training Add-On */}
            <section className="features-detail-section alt">
                <div className="features-section-container">
                    <div className="features-detail-row reverse">
                        <div className="features-detail-content">
                            <span className="splash-section-label">✦ Add-On</span>
                            <h2>Industry Ready Training</h2>
                            <p>Build a training plan, drop topics into a 365-day schedule, and the right video shows up on the right day for each user. Upgrades Daily Briefing into a full compliance system.</p>
                            <ul className="splash-bullet-list check">
                                <li>365-day topic scheduler per user or per site</li>
                                <li>Compliance tracking by category: MSHA Part 46, 48, OSHA, internal</li>
                                <li>Affidavits and viewing records auto-generated</li>
                                <li>Permanent training history per user, exportable for audits</li>
                                <li>Activation carries existing Daily Briefing topics over with no data loss</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/add-on-module-4.png" alt="Safety training" />
                        </div>
                    </div>
                </div>
            </section>

            {/* User Skillsets Add-On */}
            <section className="features-detail-section">
                <div className="features-section-container">
                    <div className="features-detail-row">
                        <div className="features-detail-content">
                            <span className="splash-section-label">✦ Add-On</span>
                            <h2>Know who's qualified for what</h2>
                            <p>Track operator certifications, equipment-specific qualifications, and expiration dates. Find the right person for the right job without asking around.</p>
                            <ul className="splash-bullet-list check">
                                <li>Skill library: certifications, equipment qualifications, internal sign-offs</li>
                                <li>Expiration tracking with auto-alerts before lapse</li>
                                <li>Filter the user list by skill: who can operate the 992 loader?</li>
                                <li>Attach certification PDFs and renewal dates per skill per user</li>
                            </ul>
                        </div>
                        <div className="features-detail-image">
                            <img src="/homepage-images/features/add-on-module-5.png" alt="Skillset tracking" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="features-pricing">
                <div className="features-section-container">
                    <span className="splash-section-label">Simple Pricing</span>
                    <h2>Start free. Pay for what you need.</h2>
                    <p className="features-pricing-subtitle">Every plan includes the core platform. Add modules in minutes, deactivate in seconds. Quoted per fleet size.</p>

                    <div className="features-pricing-grid">
                        <div className="features-pricing-card">
                            <h3>Core Platform</h3>
                            <p className="features-pricing-desc">Inspections, equipment, users, issues, daily safety briefing, forms, reports, and resources. Everything you need to get started.</p>
                            <ul className="splash-bullet-list check">
                                <li>Unlimited inspections & forms</li>
                                <li>Equipment & user management</li>
                                <li>Issue tracking & reports</li>
                                <li>Daily safety briefing</li>
                                <li>QR-coded asset workflow</li>
                                <li>Offline inspection mode</li>
                            </ul>
                            <Link to="/contact" className="features-pricing-btn outline">Get a Quote</Link>
                        </div>
                        <div className="features-pricing-card featured">
                            <div className="features-pricing-badge">Popular</div>
                            <h3>Core + Add-Ons</h3>
                            <p className="features-pricing-desc">Add Preventive Maintenance, Tire Management, Cost Tracking, Safety & Training, or User Skillsets. Pick what fits your operation.</p>
                            <ul className="splash-bullet-list check">
                                <li>Everything in Core Platform</li>
                                <li>Preventive Maintenance scheduling</li>
                                <li>Tire inventory & tracking</li>
                                <li>Cost & expense reporting</li>
                                <li>MSHA-ready Safety & Training</li>
                                <li>User Skillsets & certifications</li>
                            </ul>
                            <Link to="/register/free-demo" className="features-pricing-btn primary">Start Free 30-Day Demo</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="features-final-cta">
                <div className="features-final-cta-container">
                    <div className="features-final-cta-content">
                        <h2>Try every module free for 30 days</h2>
                        <p>Spin up an account in minutes. Activate the modules you want to test. See how your team uses it before you commit to anything. No credit card required.</p>
                        <div className="features-final-cta-buttons">
                            <Link to="/register/free-demo" className="splash-btn-primary">Start Free Demo</Link>
                            <Link to="/contact" className="splash-btn-outline">Contact Sales <span>→</span></Link>
                        </div>
                        <span className="features-final-note">Full access · All modules · No credit card · Cancel anytime</span>
                    </div>
                    <div className="features-final-cta-image">
                        <img src="/homepage-images/features/Cta-try-free.png" alt="Equipment operator" />
                    </div>
                </div>
            </section>
        </div>
    );
};
